import { List } from "immutable";
import _ from "lodash";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { NARRATIVE_VIEW } from "../../constants/identifiers";
import orientations from "../../constants/orientations";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import sortFunctions from "../../modules/items/sortFunctions";
import { narrativeSelectors } from "../../modules/narrative";
import { uiStateSelectors } from "../../modules/uiState";
import { immutableRecords } from "../../types";
import workViewSelectors from "../workView/selectors";
import { findCardWithId } from "./utils";

const {
  NARRATIVE_ADD_NODE_BUTTON_HEIGHT,
  NARRATIVE_CARD_CHILD_X_MARGIN,
  NARRATIVE_CARD_CHILD_Y_MARGIN,
  NARRATIVE_CARD_CHILD_Y_PADDING,
  NARRATIVE_CARD_MIN_INNER_HEIGHT,
  NARRATIVE_CARD_MIN_INNER_HEIGHT_WHEN_EXCLUDED,
  NARRATIVE_CARD_WIDTH,
  NARRATIVE_CARD_Y_PADDING,
  NARRATIVE_CARD_CHILDREN_MARGIN_X
} = Dimensions;

const getDepthForColumn = state => definitionSelectors.getNarrativeViewSettings(state).columnLevel;

const narrativeCardRect = (cardSize, left, top, isExcluded, fontSize) => {
  const SCALED_CARD_MIN_INNER_HEIGHT_WHEN_EXCLUDED = scalePixelsToFontSize(
    NARRATIVE_CARD_MIN_INNER_HEIGHT_WHEN_EXCLUDED,
    fontSize
  );

  const SCALED_CARD_MIN_INNER_HEIGHT = scalePixelsToFontSize(NARRATIVE_CARD_MIN_INNER_HEIGHT, fontSize);
  const SCALED_CARD_Y_PADDING = scalePixelsToFontSize(NARRATIVE_CARD_Y_PADDING, fontSize);
  const SCALED_CARD_WIDTH = scalePixelsToFontSize(NARRATIVE_CARD_WIDTH, fontSize);
  const SCALED_NARRATIVE_ADD_NODE_BUTTON_HEIGHT = scalePixelsToFontSize(NARRATIVE_ADD_NODE_BUTTON_HEIGHT, fontSize);

  const minH = isExcluded ? SCALED_CARD_MIN_INNER_HEIGHT_WHEN_EXCLUDED : SCALED_CARD_MIN_INNER_HEIGHT;
  const h =
    Math.max(cardSize.height, minH - SCALED_NARRATIVE_ADD_NODE_BUTTON_HEIGHT) +
    2 * SCALED_CARD_Y_PADDING +
    SCALED_NARRATIVE_ADD_NODE_BUTTON_HEIGHT;

  return immutableRecords.FlatRectRecord({
    width: SCALED_CARD_WIDTH,
    height: h,
    left: left,
    top: top,
    right: left + SCALED_CARD_WIDTH,
    bottom: top + h
  });
};

const createLeafDisplayCard = (id, parent, index, position, itemDisplay, depth, fontSize, typesById) => {
  // TODO: Honour isFiltered as the 4th parameter
  const rect = narrativeCardRect(itemDisplay.itemCardSize, 0, 0, itemDisplay.excludedByFilter, fontSize);
  const typeData = typeSelectors.getTypeValuesFromType(typesById.get(itemDisplay.type));

  return immutableRecords.NarrativeCardDisplayRecord({
    id,
    parent,
    index,
    rect,
    position,
    selected: false,
    depth,
    itemDisplay,
    typeData
  });
};

const layoutCards = (inputCards, orientation, parentSize, depth, fontSize, paddingLeft) => {
  const SCALED_Y_PADDING = scalePixelsToFontSize(NARRATIVE_CARD_CHILD_Y_PADDING, fontSize);
  const SCALED_X_MARGIN = scalePixelsToFontSize(NARRATIVE_CARD_CHILD_X_MARGIN, fontSize);
  const SCALED_Y_MARGIN = scalePixelsToFontSize(NARRATIVE_CARD_CHILD_Y_MARGIN, fontSize);
  const SCALED_NARRATIVE_ADD_NODE_BUTTON_HEIGHT = scalePixelsToFontSize(NARRATIVE_ADD_NODE_BUTTON_HEIGHT, fontSize);
  const SCALED_CHILDREN_X_MARGIN = scalePixelsToFontSize(NARRATIVE_CARD_CHILDREN_MARGIN_X, fontSize);

  let totalSize = { width: 0, height: 0 };
  let x = depth === 0 ? paddingLeft + SCALED_X_MARGIN : 0;
  let y = SCALED_Y_MARGIN;

  // TODO: Add wrapping alternatives!
  const updateStartPosition =
    orientation === orientations.HORIZONTAL
      ? // Horizontal next layout calc
      rect => {
        x = rect.right + SCALED_X_MARGIN;
      }
      : // Vertical next layout calc
      rect => {
        y = rect.bottom + SCALED_Y_MARGIN;
      };

  const maxWidth = inputCards.reduce((max, c) => Math.max(max, c.rect.width), 0);
  // Place side by side
  const cards = inputCards.map(c => {
    const rect = c.rect.withMutations(r => {
      const w = orientation !== orientations.HORIZONTAL ? maxWidth : r.width;
      r.set("width", w);
      r.set("left", x);
      r.set("right", x + w);
      r.set("top", y);
      r.set("bottom", y + r.height);
    });

    if (rect.right > totalSize.width) {
      totalSize.width = rect.right;
    }
    if (rect.bottom > totalSize.height) {
      totalSize.height = rect.bottom;
    }
    // Update the starting position ready for the next card
    updateStartPosition(rect);
    return c.set("rect", rect);
  });

  // Finally, add the padding around the totalSize once more
  totalSize.width += 2 * SCALED_CHILDREN_X_MARGIN;
  totalSize.height += parentSize.height + 2 * SCALED_Y_PADDING + 3 * SCALED_Y_MARGIN;

  // And also add the drop padding
  if (orientation === orientations.HORIZONTAL) {
    totalSize.width += SCALED_NARRATIVE_ADD_NODE_BUTTON_HEIGHT;
  } else {
    totalSize.height += SCALED_NARRATIVE_ADD_NODE_BUTTON_HEIGHT;
  }

  // And also space for the add node button
  if (depth === 0 && orientation === orientations.HORIZONTAL) {
    totalSize.width += NARRATIVE_CARD_WIDTH;
  }

  return {
    cards,
    totalSize
  };
};

const createDisplayCard = (
  node,
  parent,
  index,
  narrativePosition,
  itemsInGroup,
  orientationsForDepth,
  depth,
  fontSize,
  typesById,
  paddingLeft
) => {
  const id = node.id;
  const itemDisplay = itemsInGroup.find(obj => obj.id === id);

  if (!itemDisplay) {
    return null;
  }

  const itemsInGroupIds = itemsInGroup.map(i => i.id);
  const hasChildren = node.children.find(obj => itemsInGroupIds.includes(obj.id));

  const layoutOrientation = orientationsForDepth.length > depth ? orientationsForDepth[depth] : orientations.VERTICAL;

  // Bail out for leaf nodes, no recursive children to worry about.
  if (!hasChildren) {
    return createLeafDisplayCard(id, parent, index, narrativePosition, itemDisplay, depth, fontSize, typesById);
  }

  // Otherwise, before we can create this card at the right size we must first
  // create the child cards, then update their positions in the layout, and
  // calculate our overall size.
  let initialCards = node.children
    .map((childNode, i) => {
      const childPosition = narrativePosition.push(i + 1);
      return createDisplayCard(
        childNode,
        id,
        i,
        childPosition,
        itemsInGroup,
        orientationsForDepth,
        depth + 1,
        fontSize,
        typesById,
        paddingLeft
      );
    })
    .filter(card => card);

  let { cards, totalSize } = layoutCards(
    initialCards,
    layoutOrientation,
    itemDisplay.itemCardSize,
    depth + 1,
    fontSize,
    paddingLeft
  );

  // Work out size of this card...
  const rect = immutableRecords.FlatRectRecord({
    left: 0,
    width: totalSize.width,
    right: totalSize.width,
    top: 0,
    height: totalSize.height,
    bottom: totalSize.height
  });

  // TODO: Add label?
  const label = null;

  const typeData = typeSelectors.getTypeValuesFromType(typesById.get(itemDisplay.type));

  return immutableRecords.NarrativeCardDisplayRecord({
    id,
    label,
    cards,
    parent,
    index,
    rect,
    position: narrativePosition,
    orientation: layoutOrientation,
    depth,
    selected: false,
    itemDisplay,
    typeData
  });
};

const getSelectedNarrativeTree = createSelector(
  state => state.data.narrative,
  uiStateSelectors.getSelectedItems,
  (narrativeTree, selectedIds) => {
    const getSelectedNarrativeNode = node => {
      let result = List();
      if (searchIds.contains(node.id)) {
        searchIds = searchIds.filter(id => id !== node.id);
        result = List([node]);
      } else if (node.children && node.children.size > 0) {
        result = node.children.reduce((childrenResult, c) => {
          const childResult = getSelectedNarrativeNode(c, searchIds);
          if (childResult.size > 0) {
            return childrenResult.concat(childResult);
          }
          return childrenResult;
        }, List());
      }
      return result;
    };

    let searchIds = selectedIds;
    return narrativeTree.set("children", getSelectedNarrativeNode(narrativeTree, searchIds));
  }
);

// Build up a tree of cards.
// This will need to be done depth-first, as the size of each card depends on its children.
// Receives: state, viewId, tree
const getNarrativeCardLayoutWithTree = createCachedSelector(
  // fetch parameters for last calculate function
  (_state, _viewId, tree) => tree,
  workViewSelectors.getItemsInGroupForView,
  getDepthForColumn,
  appStateSelectors.getFontSize,
  (state, _viewId) => definitionSelectors.getEnabledDefinitions(state).narrativeSettings.labels,
  typeSelectors.getTypesById,
  state => uiStateSelectors.getDeviceInsets(state).left,
  // And finally call it with this
  (narrativeTree, itemsInGroup, columnDepth, fontSize, _narrativeLabels, typesById, paddingLeft) => {
    const startDepth = 0;
    let layoutOrientations = new Array(columnDepth + 1);
    _.fill(layoutOrientations, orientations.HORIZONTAL);
    if (columnDepth < 0) {
      layoutOrientations = [orientations.VERTICAL];
    }
    // Recursively calculate cards from the top level
    let topLevelCards = narrativeTree.children
      .map((node, index) =>
        itemsInGroup.find(o => o.id === node.id)
          ? createDisplayCard(
            node,
            null,
            index,
            List([index + 1]),
            itemsInGroup,
            layoutOrientations,
            startDepth + 1,
            fontSize,
            typesById,
            paddingLeft
          )
          : null
      )
      .filter(card => card);

    let { cards, totalSize } = layoutCards(
      topLevelCards,
      layoutOrientations[startDepth],
      immutableRecords.FlatRectRecord(),
      startDepth,
      fontSize,
      paddingLeft
    );
    return immutableRecords.NarrativeDisplayRecord({
      cards,
      orientation: layoutOrientations[startDepth],
      size: immutableRecords.SizeRecord(totalSize)
    });
  }
)(workViewSelectors.getViewId);

const getNarrativeCardLayout = (state, viewId) => getNarrativeCardLayoutWithTree(state, viewId, state.data.narrative);
const getNarrativeSelectedCardLayout = (state, viewId) =>
  getNarrativeCardLayoutWithTree(state, viewId, getSelectedNarrativeTree(state, viewId));

const updateSelection = (card, selectedItems) => {
  let updatedCard = card;
  if (selectedItems.contains(card.id)) {
    updatedCard = card.set("selected", true);
  }
  if (updatedCard.cards.size > 0) {
    updatedCard = updatedCard.update("cards", cards => cards.map(c => updateSelection(c, selectedItems)));
  }
  return updatedCard;
};

// Merges the leaf layout with the current selection.
// Receives: state, viewId
const getNarrativeViewDataWithLayout = createCachedSelector(
  // fetch parameters for last calculate function
  (_state, _viewId, layout) => layout,
  uiStateSelectors.getSelectedItems,
  // Calculate result
  (cardLayout, selection) => {
    return cardLayout.update("cards", cards => cards.map(c => updateSelection(c, selection)));
  }
)(workViewSelectors.getViewId);

const getNarrativeViewData = getNarrativeCardLayout;
// createCachedSelector(
//   // inputs
//   state => state,
//   (_state, viewId) => viewId,
//   // output
//   getNarrativeCardLayout
// )(workViewSelectors.getViewId);

const getNarrativeViewSelectedData = createCachedSelector(
  // inputs
  state => state,
  (_state, viewId) => viewId,
  getNarrativeSelectedCardLayout,
  // outout
  getNarrativeViewDataWithLayout
)(workViewSelectors.getViewId);

// Depth first
/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getNarrativeViewSortedItemIds = createSelector(
  // fetch parameters for last calculate function
  narrativeSelectors.getNarrativeTreeIds,
  workViewSelectors.getItemsInGroupForView,
  // Calculate result
  (narrativeIds, itemsInGroup) => {
    return narrativeIds.filter(id => itemsInGroup.find(obj => obj.id === id));
  }
);

const getNarrativeViewDataForItem = createSelector(
  // fetch parameters for last calculate function
  (state, viewId, displayData, _itemId) => displayData,
  (state, viewId, displayData, itemId) => itemId,
  (state, viewId, _displayData, _itemId) => workViewSelectors.getDisplayItemsForViewType(state, NARRATIVE_VIEW, viewId),
  (state, _viewId, _displayData, _itemId) => appStateSelectors.getFontSize(state),
  (state, _viewId, _displayData, _itemId) => typeSelectors.getTypesById(state),
  // and calculate
  (displayData, itemId, displayItems, fontSize, typesById) => {
    // See if there is already a card for this item.
    let card = displayData.cards ? findCardWithId(displayData.cards, itemId) : null;
    if (!card) {
      // Otherwise, create a leaf card
      const itemDisplay = displayItems.find(obj => obj.id === itemId);
      const index = displayData.cards.size;
      card = createLeafDisplayCard(itemId, null, index, List([index + 1]), itemDisplay, 0, fontSize, typesById);
    }
    return card;
  }
);

const getNarrativeViewAvailableItems = createSelector(
  itemSelectors.getItemsById,
  definitionSelectors.getTypesById,
  narrativeSelectors.getNarrativeTreeIds,
  state => definitionSelectors.getColorMap(state),
  // Calculate result
  (itemsById, typesById, narrativeItemIds, colorMap) => {
    return itemsById
      .toList()
      .filter(item => !narrativeItemIds.includes(item.id) && typesById.get(item.type).canIncludeInNarrative)
      .sort(sortFunctions.byLabel)
      .map(c => c.set("color", itemSelectors.getItemSwatchColor(colorMap, c)));
  }
);

export default {
  getNarrativeViewData,
  getNarrativeViewSelectedData,
  getNarrativeViewDataForItem,
  getNarrativeViewSortedItemIds,
  getNarrativeViewAvailableItems,
  getDepthForColumn
};
